import { NavNode, VenueData, LocationEntry } from './venueData';

export interface PathResult {
  nodes: string[];
  distance: number;
}

/**
 * Multi-source Multi-target A* Pathfinding.
 * Strictly avoids transit through other rooms.
 */
export function findPathMulti(
  startNodes: { id: string, spaceId: string }[],
  endNodes: { id: string, spaceId: string }[],
  data: VenueData
): PathResult | null {
  if (!startNodes.length || !endNodes.length) return null;

  const startNodeIds = startNodes.map(n => n.id);
  const endNodeIds = endNodes.map(n => n.id);
  const endNodesSet = new Set(endNodeIds);
  
  const startSpaceIds = new Set(startNodes.map(n => n.spaceId));
  const endSpaceIds = new Set(endNodes.map(n => n.spaceId));

  // Identify all "Room" spaces from locations to penalize them as transit
  const roomSpaceIds = new Set<string>();
  data.locations.forEach(loc => {
    roomSpaceIds.add(loc.spaceId);
  });

  const nodeById = data.nodeById;
  const targetNodes = endNodeIds.map(id => nodeById.get(id)).filter(Boolean) as NavNode[];
  
  const openSet = new Set<string>(startNodeIds);
  const cameFrom = new Map<string, string>();
  const gScore = new Map<string, number>(); 
  const fScore = new Map<string, number>(); 

  for (const id of startNodeIds) {
    const node = nodeById.get(id);
    if (!node) continue;
    gScore.set(id, 0);
    fScore.set(id, getMinHeuristic(node, targetNodes));
  }

  let iterations = 0;
  const maxIterations = 30000;

  while (openSet.size > 0 && iterations < maxIterations) {
    iterations++;
    
    let currentId = '';
    let minF = Infinity;
    for (const id of openSet) {
      const score = fScore.get(id) ?? Infinity;
      if (score < minF) {
        minF = score;
        currentId = id;
      }
    }

    if (!currentId) break;
    if (endNodesSet.has(currentId)) {
      return reconstructPath(cameFrom, currentId, gScore.get(currentId)!);
    }

    openSet.delete(currentId);
    const currentNode = nodeById.get(currentId)!;

    for (const neighbor of currentNode.neighbors) {
      const neighborId = neighbor.id;
      const neighborNode = nodeById.get(neighborId);
      if (!neighborNode) continue;

      let penalty = 1;
      
      // RULE: Do not enter other rooms as shortcuts
      // If the node belongs to a space that is a "Room"
      // AND it's not our origin or destination
      for (const spaceId of neighborNode.space) {
        if (roomSpaceIds.has(spaceId) && !startSpaceIds.has(spaceId) && !endSpaceIds.has(spaceId)) {
          penalty = 5000; // Extremely high penalty to forbid room-transit
        }
        
        // Also penalize the quadrangle specifically (often contains shortcuts)
        if (spaceId === 's_436ac25ba8c739e1' && !startSpaceIds.has(spaceId) && !endSpaceIds.has(spaceId)) {
          penalty = 100; 
        }
      }

      const tentativeGScore = (gScore.get(currentId) ?? Infinity) + (neighbor.weight * penalty);

      if (tentativeGScore < (gScore.get(neighborId) ?? Infinity)) {
        cameFrom.set(neighborId, currentId);
        gScore.set(neighborId, tentativeGScore);
        fScore.set(neighborId, tentativeGScore + getMinHeuristic(neighborNode, targetNodes));
        openSet.add(neighborId);
      }
    }
  }

  return null;
}

function getMinHeuristic(node: NavNode, targets: NavNode[]): number {
  let minH = Infinity;
  for (const target of targets) {
    const h = estimateDistance(node, target);
    if (h < minH) minH = h;
  }
  return minH;
}

function estimateDistance(a: NavNode, b: NavNode): number {
  const dx = (a.lng - b.lng) * 111320 * Math.cos(a.lat * Math.PI / 180);
  const dy = (a.lat - b.lat) * 110540;
  return Math.sqrt(dx * dx + dy * dy) * 30; 
}

function reconstructPath(cameFrom: Map<string, string>, current: string, distance: number): PathResult {
  const totalPath = [current];
  while (cameFrom.has(current)) {
    current = cameFrom.get(current)!;
    totalPath.unshift(current);
  }
  return { nodes: totalPath, distance };
}
